import React from "react";
import { isNumber, isObject } from "@common/helpers/typeGuards";
import { currencyFormatter } from "@common/helpers/currency";
import {
    RuleConditionAmount,
    TransactionRuleCondition,
} from "@common/types/transactionRule";
import { Tag } from "@design-system/Tag/Tag";
import { RuleTagRow } from "../RuleTagRow";
import { RULE_TAG_PROPS } from "../ruleTagUtils";

interface Props {
    condition: TransactionRuleCondition;
}

export const RuleAmountCondition: React.FC<Props> = ({ condition }) => {
    if (
        !isObject(condition.value) ||
        (!("min" in condition.value) && !("max" in condition.value))
    ) {
        return null;
    }

    const value = condition.value as RuleConditionAmount;
    const isCredit =
        (isNumber(value.min) && value.min >= 0) ||
        (isNumber(value.max) && value.max > 0);
    const transactionType = isCredit ? "Cash in" : "Cash out";

    if (isNumber(value.min) && isNumber(value.max)) {
        if (value.min === value.max) {
            return (
                <DisplayExactValue
                    value={value.min}
                    transactionType={transactionType}
                />
            );
        }
        return (
            <DisplayRange
                min={value.min}
                max={value.max}
                isCredit={isCredit}
                transactionType={transactionType}
            />
        );
    }

    if (isNumber(value.min)) {
        return (
            <DisplayMinValue
                value={value.min}
                isCredit={isCredit}
                transactionType={transactionType}
            />
        );
    }

    if (isNumber(value.max)) {
        return (
            <DisplayMaxValue
                value={value.max}
                isCredit={isCredit}
                transactionType={transactionType}
            />
        );
    }

    return null;
};

interface DisplayRangeValueProps {
    min: number;
    max: number;
    isCredit: boolean;
    transactionType: string;
}

const DisplayRange: React.FC<DisplayRangeValueProps> = ({
    min,
    max,
    isCredit,
    transactionType,
}) => {
    const lowerValue = isCredit ? min : max;
    const upperValue = isCredit ? max : min;

    return (
        <RuleTagRow label="Amount" type="condition">
            <Tag {...RULE_TAG_PROPS}>{transactionType}</Tag>
            <Tag {...RULE_TAG_PROPS}>
                between {currencyFormatter.format(Math.abs(lowerValue))} and{" "}
                {currencyFormatter.format(Math.abs(upperValue))}
            </Tag>
        </RuleTagRow>
    );
};

interface DisplaySingleValueProps {
    value: number;
    isCredit?: boolean;
    transactionType: string;
}

const DisplayMinValue: React.FC<DisplaySingleValueProps> = ({
    value,
    isCredit,
    transactionType,
}) => (
    <RuleTagRow label="Amount" type="condition">
        <Tag {...RULE_TAG_PROPS}>{transactionType}</Tag>
        {value ? (
            <Tag {...RULE_TAG_PROPS}>
                {isCredit ? "is above" : "is below"}{" "}
                {currencyFormatter.format(Math.abs(value))}
            </Tag>
        ) : null}
    </RuleTagRow>
);

const DisplayMaxValue: React.FC<DisplaySingleValueProps> = ({
    value,
    isCredit,
    transactionType,
}) => (
    <RuleTagRow label="Amount" type="condition">
        <Tag {...RULE_TAG_PROPS}>{transactionType}</Tag>
        {value ? (
            <Tag {...RULE_TAG_PROPS}>
                {isCredit ? "is below" : "is above"}{" "}
                {currencyFormatter.format(Math.abs(value))}
            </Tag>
        ) : null}
    </RuleTagRow>
);

const DisplayExactValue: React.FC<
    Omit<DisplaySingleValueProps, "isCredit">
> = ({ value, transactionType }) => (
    <RuleTagRow label="Amount" type="condition">
        <Tag {...RULE_TAG_PROPS}>{transactionType}</Tag>
        {value ? (
            <Tag {...RULE_TAG_PROPS}>
                is {currencyFormatter.format(Math.abs(value))}
            </Tag>
        ) : null}
    </RuleTagRow>
);
